// ─────────────────────────────────────────────────────────────────────────────
// 🔑 REAPPLY, ON A DECLINED FILE (Jesse, 2026-10-22 — backend constants/adverseReapply.reapplyStatus). Staff see the
// date the borrower may apply again (the same rule step 8 and Eva state, and the server enforces) and can open reapply
// early with a reason, which is recorded on the file and as an application event.
// GET / POST /api/admin/borrowers/:id/reapply-status | reapply-early.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const day = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '');

export default function ReapplyEarlyCard({ borrowerId, token }) {
  const [st, setSt] = useState(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const H = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/admin/borrowers/${borrowerId}/reapply-status`, { headers: H });
      setSt(r.ok ? await r.json() : { error: true });
    } catch (_) { setSt({ error: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borrowerId, token]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API}/api/admin/borrowers/${borrowerId}/reapply-early`, { method: 'POST', headers: H, body: JSON.stringify({ reason }) });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) { setError(b.error || 'Could not open reapply.'); return; }
      setOpen(false); setReason(''); await load();
    } catch (_) { setError('Could not open reapply.'); } finally { setBusy(false); }
  };

  if (!st || (!st.error && !st.declined)) return null;
  const line = st.error ? 'Could not read the reapply status.'
    : st.early ? `Opened early by staff on ${day(st.early.at)} — ${st.early.reason}`
      : st.state === 'open' ? 'The borrower can apply again now.'
        : st.state === 'wait' ? `The borrower can apply again on ${day(st.opensOn)}.`
          : 'No reapply date on file (no reasons, or an undated event) — the borrower is told to message the team.';
  return (
    <div data-testid="admin-reapply-card" style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', margin: '12px 0', background: '#fff' }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#111827', marginBottom: 4 }}>Reapply</div>
      <div data-testid="admin-reapply-line" style={{ fontSize: 12.5, color: '#374151', lineHeight: 1.5 }}>{line}</div>
      {!st.error && !st.early && st.state !== 'open' && (
        open ? (
          <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
            <textarea data-testid="admin-reapply-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Why reapply is opened early (e.g. a credit report error the bureau has corrected)"
              style={{ width: '100%', boxSizing: 'border-box', fontSize: 12.5, padding: 8, borderRadius: 8, border: '1px solid #d1d5db' }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" data-testid="admin-reapply-confirm" disabled={busy || reason.trim().length < 10} onClick={submit}
                style={{ padding: '6px 12px', fontSize: 12.5, fontWeight: 700, borderRadius: 8, border: 'none', background: '#3a6ad9', color: '#fff', opacity: busy || reason.trim().length < 10 ? 0.6 : 1 }}>
                {busy ? 'Opening…' : 'Open reapply now'}
              </button>
              <button type="button" onClick={() => { setOpen(false); setError(null); }} style={{ padding: '6px 12px', fontSize: 12.5, borderRadius: 8, border: '1px solid #d1d5db', background: '#fff' }}>Cancel</button>
            </div>
          </div>
        ) : (
          <button type="button" data-testid="admin-reapply-open" onClick={() => setOpen(true)}
            style={{ marginTop: 8, padding: '6px 12px', fontSize: 12.5, fontWeight: 700, borderRadius: 8, border: '1px solid #d1d5db', background: '#fff' }}>
            Open reapply early…
          </button>
        )
      )}
      {error && <div style={{ fontSize: 12, color: '#9a3412', marginTop: 6 }}>{error}</div>}
    </div>
  );
}
